'use client'

import { useRef, useState, useEffect, Fragment } from 'react'
import { Col, Separator } from '@repo/ui/components'
import { Typography } from '@repo/ui/components/typography'
import { Icon } from '@repo/ui/components/icon'
import { useTranslate, useLanguage } from '@repo/shared/hooks'
import { Link } from '@repo/shared/i18n/navigation'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { cn } from '@repo/ui/lib/utils'

import type { MenuBarTradeModalProps } from './menu-bar.trade-modal.types'

export const MenuBarTradeModal = ({
  open,
  onOpenChange,
  buttonRef,
  items,
}: MenuBarTradeModalProps) => {
  const { t } = useTranslate()
  const locale = useLanguage()
  const isRTL = locale === 'fa'
  const overlayRef = useRef<HTMLDivElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  // Handle visibility state
  useEffect(() => {
    if (open) {
      setIsVisible(true)
    }
  }, [open])

  useGSAP(() => {
    if (!overlayRef.current || !backdropRef.current) return

    if (open && buttonRef?.current) {
      // Get button position
      const buttonRect = buttonRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth

      // Calculate position from button
      // For RTL: use bottom-left, for LTR: use bottom-right
      const bottom = viewportHeight - buttonRect.bottom
      const right = viewportWidth - buttonRect.right
      const left = buttonRect.left

      // Rotate button
      // gsap.to(buttonRef.current, {
      //   rotation: 180,
      //   duration: 0.4,
      //   ease: 'power2.out',
      // })

      // Set initial position at button location
      const positionProps = isRTL
        ? {
            left: `${left}px`,
            right: 'auto',
          }
        : {
            right: `${right}px`,
            left: 'auto',
          }

      gsap.set(overlayRef.current, {
        scale: 0,
        transformOrigin: isRTL ? 'bottom left' : 'bottom right',
        bottom: `${bottom}px`,
        ...positionProps,
        x: 0,
        y: 0,
      })

      // Animate backdrop
      gsap.fromTo(
        backdropRef.current,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.3,
          ease: 'power2.out',
        },
      )

      // Animate overlay from button position
      gsap.to(overlayRef.current, {
        scale: 1,
        duration: 0.4,
        ease: 'back.out(1.2)',
      })

      // Rotate close button on open
      if (closeButtonRef.current) {
        gsap.fromTo(
          closeButtonRef.current,
          { rotation: -180, opacity: 0 },
          {
            rotation: 0,
            opacity: 1,
            duration: 0.4,
            ease: 'back.out(1.2)',
            delay: 0.2,
          },
        )
      }
    } else if (!open && isVisible) {
      // Rotate button back
      // if (buttonRef?.current) {
      //   gsap.to(buttonRef.current, {
      //     rotation: 0,
      //     duration: 0.3,
      //     ease: 'power2.in',
      //   })
      // }

      // Rotate close button on close
      if (closeButtonRef.current) {
        gsap.to(closeButtonRef.current, {
          rotation: -180,
          opacity: 0,
          duration: 0.2,
          ease: 'power2.in',
        })
      }

      // Animate out
      const timeline = gsap.timeline({
        onComplete: () => {
          setIsVisible(false)
        },
      })
      timeline.to(overlayRef.current, {
        scale: 0,
        duration: 0.3,
        ease: 'power2.in',
      })
      timeline.to(
        backdropRef.current,
        {
          opacity: 0,
          duration: 0.2,
          ease: 'power2.out',
        },
        '-=0.2',
      )
    }
  }, [open, buttonRef, isRTL, isVisible])

  const handleCloseModal = () => {
    onOpenChange(false)
  }

  if (!isVisible) return null

  return (
    <>
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className='fixed inset-0 bg-neutral-main-40 z-[100]'
        onClick={handleCloseModal}
      />
      {/* Overlay */}
      <div
        ref={overlayRef}
        className={cn(
          'fixed z-[101]',
          'bg-base-radial-gradient rounded-xl',
          'shadow-lg',
          'max-w-[255px]',
        )}
        style={{
          transformOrigin: isRTL ? 'bottom left' : 'bottom right',
        }}
      >
        <Col className='gap-lg'>
          {items.map((item, index) => (
            <Fragment key={item.href}>
              <Col
                className={cn(
                  item.disabled &&
                    'opacity-50 cursor-not-allowed  pointer-events-none',
                )}
                as={Link}
                href={item.href}
                buttonClassName={cn(
                  'items-start cursor-pointer gap-sm px-md',
                  index === 0 ? 'pt-xl' : '',
                )}
                onClick={handleCloseModal}
              >
                <Typography weight='bold' size='md' color='text-base-main-100'>
                  {t(item.label)}
                </Typography>
                <Typography
                  weight='regular'
                  size='xs'
                  color='text-base-main-80'
                >
                  {t(item.description)}
                </Typography>
              </Col>
              <Separator className='bg-base-main-5' />
            </Fragment>
          ))}

          <Col className=' items-end px-sm pb-sm'>
            <button
              ref={closeButtonRef}
              onClick={handleCloseModal}
              className='cursor-pointer'
            >
              <Icon
                name='Xmark'
                size='md'
                color='fill-neutral-surface'
                className='opacity-60 hover:opacity-100 transition-opacity'
              />
            </button>
          </Col>
        </Col>
      </div>
    </>
  )
}
